import { Component, computed, inject, Input, OnInit, Signal, signal } from "@angular/core";
import { CourseTest, MovePossibilitiesFor1Test, TestMovePossibilities } from "@app/core/domain/course-test";
import { Period, PeriodIdentification, StudyYear, YearIdentification } from "@app/core/domain/plan-details";
import {
  IonButton,
  IonContent,
  IonIcon,
  IonItem,
  IonList,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  ModalController,
} from "@ionic/angular/standalone";
import { InputCustomEvent } from "@ionic/core";
import { TranslatePipe } from "@ngx-translate/core";
import {
  AvailableSlot,
  PeriodSelectionGridComponent,
} from "@shared/components/period-selection-grid/period-selection-grid.component";
import { PlanStateService } from "@shared/services/plan-state.service";

export interface SelectedTestPeriod {
  testCode: string;
  year: YearIdentification;
  period: PeriodIdentification;
}

/**
 * Modal component to specify to which study year and period a test should be moved.
 */
@Component({
  selector: "pa-move-test-modal",
  templateUrl: "./move-test-modal.component.html",
  styleUrls: ["./move-test-modal.component.scss"],
  imports: [
    IonButton,
    IonContent,
    IonIcon,
    IonItem,
    IonList,
    IonSelect,
    IonSelectOption,
    IonTitle,
    IonToolbar,
    TranslatePipe,
    PeriodSelectionGridComponent,
  ],
})
export class MoveTestModalComponent implements OnInit {
  @Input() movePossibilities!: TestMovePossibilities;
  @Input() studyYears!: StudyYear[];
  @Input() courseTests!: CourseTest[];

  private readonly modalCtrl = inject(ModalController);
  private readonly planStateService = inject(PlanStateService);

  protected selectedTest = signal<MovePossibilitiesFor1Test | null>(null);

  ngOnInit(): void {
    // enrich the move possibilities with test descriptions from the courseTests input
    this.movePossibilities.forEach((possibility) => {
      const matchingCourseTest = this.courseTests.find((test) => test.testCode === possibility.testCode);
      if (matchingCourseTest) {
        possibility.testDescription = matchingCourseTest.testDescription;
      }
    });

    // if there is only one test, select it by default
    if (this.movePossibilities.length === 1) {
      this.setSelectedTest(this.movePossibilities[0]);
    } else {
      // sort the move possibilities by test description
      this.movePossibilities.sort((a, b) => a.testDescription!.localeCompare(b.testDescription!));
    }
  }

  protected availableYears: Signal<StudyYear[]> = computed(() => {
    // return the StudyYears objects that match the yearNrs in the selected test's move possibilities
    if (!this.selectedTest()) return [];
    const yearNrs = new Set(this.selectedTest()!.movePossibilities.map((possibility) => possibility.yearNr));
    return this.studyYears.filter((studyYear) => yearNrs.has(studyYear.yearNr));
  });

  protected gridYears: Signal<StudyYear[]> = computed(() => {
    const currentYear = this.planStateService.currentSelectedPlan()?.currentYear;
    if (!currentYear) return this.studyYears;
    return this.studyYears.filter((year) => year.academicYear >= currentYear);
  });

  protected gridPeriods: Signal<Period[]> = computed(() => {
    return this.studyYears[0]?.periods || [];
  });

  protected gridAvailableSlots: Signal<AvailableSlot[]> = computed(() => {
    if (!this.selectedTest()) return [];
    return this.selectedTest()!.movePossibilities.map((possibility) => ({
      yearNr: possibility.yearNr,
      periodNr: possibility.periodNr,
    }));
  });

  private setSelectedTest(test: MovePossibilitiesFor1Test | null): void {
    this.selectedTest.set(test);
  }

  protected onTestChange($event: Event): void {
    const changeEvent = $event as InputCustomEvent;
    const selectedMovePossibilitiesFor1Test = changeEvent.detail.value as unknown as MovePossibilitiesFor1Test;
    this.setSelectedTest(selectedMovePossibilitiesFor1Test);
  }

  protected cancel(): void {
    this.modalCtrl.dismiss(null, "cancel").catch((err) => {
      console.error("Error canceling Move test modal:", err);
    });
  }

  protected onGridSelectedPeriod(event: { year: YearIdentification; period: PeriodIdentification }): void {
    const selectedTestPeriod: SelectedTestPeriod = {
      testCode: this.selectedTest()!.testCode,
      year: event.year,
      period: event.period,
    };
    this.modalCtrl.dismiss(selectedTestPeriod).catch((err) => {
      console.error("Error dismissing Move test modal:", err, selectedTestPeriod);
    });
  }
}
